/**
 * IndexedDB store for drawing edits that haven't been confirmed by the server.
 *
 * Acts as a safety net in two scenarios:
 *   1. The retry loop in App.tsx#doCloudSync exhausted attempts.
 *   2. The keepalive save on unload skipped the request (payload >60KB).
 *
 * When a drawing is opened, App.tsx checks here for a pending sync that is
 * newer than the server copy (compared by versionSum) and re-issues the save.
 */
import { createStore, get, set, del } from "idb-keyval";

const pendingSyncStore = createStore(
  "drawings-pending-sync-db",
  "pending-store",
);

export interface PendingSync {
  drawingId: string;
  elements: any[];
  appState: Record<string, any>;
  files: Record<string, any>;
  /** Wall-clock time when the pending state was recorded. */
  savedAt: number;
  /** Sum of element.version across all elements — used to compare freshness. */
  versionSum: number;
}

export const PendingSyncStore = {
  async save(data: PendingSync): Promise<void> {
    await set(data.drawingId, data, pendingSyncStore);
  },
  async load(drawingId: string): Promise<PendingSync | undefined> {
    return get<PendingSync>(drawingId, pendingSyncStore);
  },
  async clear(drawingId: string): Promise<void> {
    await del(drawingId, pendingSyncStore);
  },
};

export function computeVersionSum(elements: readonly any[]): number {
  let sum = 0;
  for (const el of elements) {
    sum += el.version || 0;
  }
  return sum;
}
